import axiosInstance from "@/api/axiosInstance";
import {
  UnifiedCall,
  ScoringRubric,
  NotificationRecipient,
  EmailTemplate,
  NotificationSettings,
  DashboardMetrics,
  CallFilters,
  PaginatedCallsResponse,
} from "../types";

const CALL_INTELLIGENCE_BASE = "/api/private/call-intelligence";

export const callIntelligenceApi = {
  // Calls
  async getCalls(filters?: CallFilters): Promise<PaginatedCallsResponse> {
    try {
      const params: Record<string, string | number | boolean> = {};
      if (filters?.searchText) params.search = filters.searchText;
      if (filters?.dateRange?.start) params.dateFrom = filters.dateRange.start;
      if (filters?.dateRange?.end) params.dateTo = filters.dateRange.end;
      if (filters?.page) params.page = filters.page;
      if (filters?.pageSize) params.limit = filters.pageSize;
      if (filters?.sortBy) params.sortBy = filters.sortBy;
      if (filters?.sortOrder) params.sortOrder = filters.sortOrder;

      const response = await axiosInstance.get(`${CALL_INTELLIGENCE_BASE}/calls`, { params });
      return response.data as PaginatedCallsResponse;
    } catch (error) {
      console.error("Error fetching calls:", error);
      // Fallback to old endpoints during migration
      try {
        const [salesCalls, transcripts] = await Promise.all([
          axiosInstance.get("/api/private/sales-coaching/calls"),
          axiosInstance.get("/api/private/transcripts/calls"),
        ]);
        const merged = this.mergeCalls(salesCalls.data, transcripts.data);
        return { data: merged, meta: { totalCount: merged.length, page: 1, pageSize: merged.length } };
      } catch (fallbackError) {
        console.error("Fallback failed:", fallbackError);
        return { data: [], meta: { totalCount: 0, page: 1, pageSize: 10 } };
      }
    }
  },

  async getCallById(id: string): Promise<UnifiedCall | null> {
    try {
      const response = await axiosInstance.get(`${CALL_INTELLIGENCE_BASE}/calls/${id}`);
      // Backend now returns all rubric data including rubricName, rubricEvaluationCriteria, and criteriaScoresList
      return response.data as UnifiedCall;
    } catch (error) {
      console.error("Error fetching call:", error);
      return null;
    }
  },

  async scoreCall(id: string, rubricId?: number): Promise<UnifiedCall> {
    const response = await axiosInstance.post(`${CALL_INTELLIGENCE_BASE}/calls/${id}/score`, { rubricId });
    return response.data;
  },

  async updateCallNotes(id: string, notes: string): Promise<UnifiedCall> {
    const response = await axiosInstance.patch(`${CALL_INTELLIGENCE_BASE}/calls/${id}/notes`, { notes });
    return response.data;
  },

  // Scoring
  async getRubrics(): Promise<ScoringRubric[]> {
    try {
      const response = await axiosInstance.get(`${CALL_INTELLIGENCE_BASE}/scoring/rubrics`);
      return response.data;
    } catch {
      // Fallback to sales-coaching endpoint
      const response = await axiosInstance.get("/api/private/sales-coaching/rubrics");
      return response.data;
    }
  },

  async createRubric(rubric: Partial<ScoringRubric>): Promise<ScoringRubric> {
    try {
      const response = await axiosInstance.post(`${CALL_INTELLIGENCE_BASE}/scoring/rubrics`, rubric);
      return response.data;
    } catch {
      const response = await axiosInstance.post("/api/private/sales-coaching/rubrics", rubric);
      return response.data;
    }
  },

  async updateRubric(
    id: number,
    rubric: Partial<ScoringRubric>,
  ): Promise<ScoringRubric> {
    try {
      const response = await axiosInstance.put(`${CALL_INTELLIGENCE_BASE}/scoring/rubrics/${id}`, rubric);
      return response.data;
    } catch {
      const response = await axiosInstance.put(`/api/private/sales-coaching/rubrics/${id}`, rubric);
      return response.data;
    }
  },

  async deleteRubric(id: number): Promise<void> {
    try {
      await axiosInstance.delete(`${CALL_INTELLIGENCE_BASE}/scoring/rubrics/${id}`);
    } catch {
      await axiosInstance.delete(`/api/private/sales-coaching/rubrics/${id}`);
    }
  },

  // Notifications
  async getNotificationSettings(): Promise<NotificationSettings> {
    try {
      const response = await axiosInstance.get(`${CALL_INTELLIGENCE_BASE}/notifications/settings`);
      return response.data;
    } catch {
      // Fallback to transcripts endpoint
      const response = await axiosInstance.get("/api/private/transcripts/notification-settings");
      return response.data;
    }
  },

  async updateNotificationSettings(
    settings: NotificationSettings,
  ): Promise<NotificationSettings> {
    try {
      const response = await axiosInstance.put(`${CALL_INTELLIGENCE_BASE}/notifications/settings`, settings);
      return response.data;
    } catch {
      const response = await axiosInstance.put("/api/private/transcripts/notification-settings", settings);
      return response.data;
    }
  },

  async getRecipients(): Promise<NotificationRecipient[]> {
    try {
      const response = await axiosInstance.get(`${CALL_INTELLIGENCE_BASE}/notifications/recipients`);
      return response.data;
    } catch {
      const response = await axiosInstance.get("/api/private/transcripts/notification-recipients");
      return response.data;
    }
  },

  async addRecipient(
    recipient: Partial<NotificationRecipient>,
  ): Promise<NotificationRecipient> {
    try {
      const response = await axiosInstance.post(`${CALL_INTELLIGENCE_BASE}/notifications/recipients`, recipient);
      return response.data;
    } catch {
      const response = await axiosInstance.post("/api/private/transcripts/notification-recipients", recipient);
      return response.data;
    }
  },

  async updateRecipient(
    id: number,
    recipient: Partial<NotificationRecipient>,
  ): Promise<NotificationRecipient> {
    try {
      const response = await axiosInstance.put(`${CALL_INTELLIGENCE_BASE}/notifications/recipients/${id}`, recipient);
      return response.data;
    } catch {
      const response = await axiosInstance.put(`/api/private/transcripts/notification-recipients/${id}`, recipient);
      return response.data;
    }
  },

  async deleteRecipient(id: number): Promise<void> {
    try {
      await axiosInstance.delete(`${CALL_INTELLIGENCE_BASE}/notifications/recipients/${id}`);
    } catch {
      await axiosInstance.delete(`/api/private/transcripts/notification-recipients/${id}`);
    }
  },

  async getEmailTemplates(): Promise<EmailTemplate[]> {
    try {
      const response = await axiosInstance.get(`${CALL_INTELLIGENCE_BASE}/notifications/templates`);
      return response.data;
    } catch {
      const response = await axiosInstance.get("/api/private/transcripts/email-templates");
      return response.data;
    }
  },

  async updateEmailTemplate(
    id: number,
    template: Partial<EmailTemplate>,
  ): Promise<EmailTemplate> {
    try {
      const response = await axiosInstance.put(`${CALL_INTELLIGENCE_BASE}/notifications/templates/${id}`, template);
      return response.data;
    } catch {
      const response = await axiosInstance.put(`/api/private/transcripts/email-templates/${id}`, template);
      return response.data;
    }
  },

  // Analytics
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    try {
      const response = await axiosInstance.get(`${CALL_INTELLIGENCE_BASE}/analytics/dashboard`);
      return response.data;
    } catch {
      // Generate mock metrics from existing data
      const response = await this.getCalls();
      return this.generateDashboardMetrics(response.data);
    }
  },

  // Helper methods for migration period
  mergeCalls(salesCalls: UnifiedCall[], transcripts: UnifiedCall[]): UnifiedCall[] {
    const unified: UnifiedCall[] = [];

    // Convert sales calls
    salesCalls?.forEach((call) => {
      unified.push({
        id: call.id || String(call.recordingId),
        recordingId: call.recordingId,
        salesRepEmail: call.salesRepEmail,
        salesRepName: call.salesRepName,
        prospectCompany: call.prospectCompany,
        dealStage: call.dealStage,
        meetingDate: call.meetingDate || call.createdAt,
        duration: call.duration,
        totalScore: call.totalScore,
        criteriaScores: call.criteriaScores,
        aiSummary: call.aiSummary,
        source: "zoom",
        createdAt: call.createdAt,
        updatedAt: call.updatedAt,
      });
    });

    // Convert transcripts
    transcripts?.forEach((transcript) => {
      unified.push({
        id: transcript.id,
        transcriptText: transcript.transcriptText,
        meetingDate: transcript.meetingDate,
        attendees: transcript.attendees,
        duration: transcript.duration,
        totalScore: transcript.totalScore,
        aiSummary: transcript.aiSummary,
        source: "manual",
        createdAt: transcript.createdAt,
        updatedAt: transcript.updatedAt,
      });
    });

    return unified;
  },

  generateDashboardMetrics(calls: UnifiedCall[]): DashboardMetrics {
    const thisWeek = new Date();
    thisWeek.setDate(thisWeek.getDate() - 7);

    // Build date keys for the last 7 days
    const dayKeys: string[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dayKeys.push(d.toISOString().split("T")[0]);
    }

    // Single pass: accumulate all metrics at once
    let callsThisWeekCount = 0;
    let scoreSum = 0;
    let scoreCount = 0;
    const repScores = new Map<string, number[]>();
    const dayData = new Map<string, { callCount: number; scoreSum: number; scoreCount: number }>();
    dayKeys.forEach((key) => dayData.set(key, { callCount: 0, scoreSum: 0, scoreCount: 0 }));

    calls.forEach((call) => {
      if (new Date(call.meetingDate) >= thisWeek) {
        callsThisWeekCount++;
      }

      if (call.totalScore) {
        scoreSum += call.totalScore;
        scoreCount++;

        if (call.salesRepName) {
          if (!repScores.has(call.salesRepName)) {
            repScores.set(call.salesRepName, []);
          }
          repScores.get(call.salesRepName)!.push(call.totalScore);
        }
      }

      const dateStr = call.meetingDate?.split("T")[0];
      const day = dayData.get(dateStr);
      if (day) {
        day.callCount++;
        if (call.totalScore) {
          day.scoreSum += call.totalScore;
          day.scoreCount++;
        }
      }
    });

    let topPerformer = { name: "N/A", score: 0 };
    repScores.forEach((scores, name) => {
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      if (avg > topPerformer.score) {
        topPerformer = { name, score: Math.round(avg) };
      }
    });

    const trends = dayKeys.map((date) => {
      const day = dayData.get(date)!;
      return {
        date,
        averageScore: day.scoreCount > 0 ? Math.round(day.scoreSum / day.scoreCount) : 0,
        callCount: day.callCount,
      };
    });

    return {
      totalCalls: calls.length,
      averageScore: scoreCount > 0 ? Math.round(scoreSum / scoreCount) : 0,
      callsThisWeek: callsThisWeekCount,
      topPerformer,
      scoringTrends: trends,
    };
  },
};
